import { BellOutlined, CloseOutlined } from '@/app/icons';
import { App, Button, Card, Flex, Typography, theme } from 'antd';
import { useEffect, useState } from 'react';
import { formatFullDate, weekdayIndex } from '@/domain/dates';
import { inAppReminders } from '@/domain/push';
import { useNow } from '@/hooks/use-now';
import { useCompanySettings } from '@/hooks/use-records';
import { usePushStore } from '@/stores/push-store';

const DISMISSED_KEY = 'halo-push-dismissed';
const SHOWN_KEY = 'halo-reminders-shown';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Lịch nhắc đã hiện trong app HÔM NAY (trên máy này) — để hai thẻ cùng mở không hiện hai lần. */
function readShown(day: string): Set<string> {
  try {
    const stored = JSON.parse(localStorage.getItem(SHOWN_KEY) ?? 'null') as {
      day?: unknown;
      ids?: unknown;
    } | null;
    if (stored?.day === day && Array.isArray(stored.ids)) {
      return new Set(stored.ids.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    /* dữ liệu cũ hỏng → coi như chưa hiện gì */
  }
  return new Set();
}

function markShown(day: string, ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(SHOWN_KEY, JSON.stringify({ day, ids: [...ids] }));
  } catch {
    /* không nhớ được thì thẻ khác có thể hiện lại — không sao */
  }
}

/** Bật thông báo rồi báo kết quả cho người dùng — dùng chung cho lời mời và mục trong menu tài khoản. */
export function useEnablePush(): () => Promise<void> {
  const { message } = App.useApp();
  const enable = usePushStore((state) => state.enable);
  return async () => {
    const problem = await enable();
    if (problem) void message.warning(problem, 6);
    else void message.success('Đã bật — máy này sẽ đổ chuông khi tới giờ nhắc');
  };
}

/**
 * CHUÔNG NHẮC (03/10/2026, chủ dự án: «lịch thông báo mọi người 17h00 làm báo cáo, 8h30 chấm công» ·
 * «chuông báo về điện thoại») — hai thứ đi theo khung ứng dụng:
 *
 *  1. LỜI MỜI BẬT CHUÔNG trên máy chưa bật: một hàng gọn ở đầu trang. Mỗi người phải tự bấm một lần trên
 *     máy mình — trình duyệt không cho ai bật hộ. Đóng đi thì máy ấy không bị mời lại (vẫn bật được từ
 *     menu tài khoản).
 *  2. NHẮC TRONG APP cho máy chưa bật thông báo đẩy mà ứng dụng đang mở: tới giờ thì hiện một thông báo
 *     trong trang. Máy đã bật thì thôi — chuông của hệ điều hành đã báo, không báo hai lần.
 */
export function PushNotifications() {
  const { token } = theme.useToken();
  const { notification } = App.useApp();
  const state = usePushStore((store) => store.state);
  const busy = usePushStore((store) => store.busy);
  const refresh = usePushStore((store) => store.refresh);
  const enablePush = useEnablePush();
  const { reminders } = useCompanySettings();
  const now = useNow();
  const [dismissed, setDismissed] = useState(readDismissed);

  // Vào app là đọc lại trạng thái của máy và báo lại đăng ký đang có cho máy chủ.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (state === 'on') return;
    const day = formatFullDate(now.date);
    const shown = readShown(day);
    const due = inAppReminders(reminders, weekdayIndex(now.date), now.minutes, shown);
    if (!due.length) return;
    for (const reminder of due) {
      shown.add(reminder.id);
      notification.info({
        key: `reminder-${reminder.id}`,
        title: reminder.title,
        description: reminder.body,
        duration: 0,
        placement: 'topRight',
      });
    }
    markShown(day, shown);
  }, [state, now, reminders, notification]);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* không nhớ được thì lần sau mời lại — không sao */
    }
  };

  if (state !== 'off' || dismissed) return null;
  return (
    <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '8px 12px' } }}>
      <Flex align="center" gap={10}>
        <BellOutlined style={{ fontSize: token.fontSizeXL, color: token.colorPrimary }} />
        <Typography.Text style={{ flex: 1, minWidth: 0 }}>
          Bật chuông nhắc việc trên máy này
        </Typography.Text>
        <Button type="primary" loading={busy} onClick={() => void enablePush()}>
          Bật
        </Button>
        <Button
          type="text"
          icon={<CloseOutlined />}
          aria-label="Ẩn lời mời bật chuông nhắc"
          onClick={dismiss}
        />
      </Flex>
    </Card>
  );
}
